import React, { useState } from "react"
import cn from "classnames"
import { ChevronLeftLarge, ChevronRightLarge } from "@/skylticons"
import type { ExtraImage } from "@/api/graphql"
import PreviewModal, { type PreviewImageSlide } from "./preview_modal"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"

interface SlideshowProps {
  images: Pick<ExtraImage, "id" | "png" | "png2x" | "webp" | "webp2x">[]
}

const Slideshow: React.FC<SlideshowProps> = ({ images }) => {
  const [slide, setSlide] = useState(0)
  const [openPreview, setOpenPreview] = useState(false)

  const showPreview = () => setOpenPreview(true)
  const close = () => setOpenPreview(false)

  const ssrDisabled = useSSRDisabled()
  const controls = !ssrDisabled && images.length > 1

  const buttonClick = (newSlide: number) => {
    if (newSlide < 0) {
      setSlide(images.length - 1)
    } else if (newSlide === images.length) {
      setSlide(0)
    } else {
      setSlide(newSlide)
    }
  }

  const slides: PreviewImageSlide[] = images.map(img => {
    return {
      type: "image",
      png: img.png,
      png2x: img.png2x,
      webp: img.webp,
      webp2x: img.webp2x,
    }
  })

  return (
    <>
      <div className="h-full lg:h-auto">
        <div className="relative flex h-full items-center justify-between overflow-hidden lg:h-64">
          {controls && (
            <div
              className="button button-secondary flat icon-only z-10 ml-2 xs:m-0"
              onClick={() => buttonClick(slide - 1)}
              data-gtm="lp-image-slider-left"
            >
              <ChevronLeftLarge />
            </div>
          )}
          <div
            className="absolute left-0 top-0 flex h-full transition-[left] duration-500"
            style={{ width: `${images.length * 100}%`, left: `${slide * -100}%` }}
          >
            {images.map(img => (
              <picture
                key={img.id}
                className={cn(img.id, "mx-14 flex flex-1 flex-col items-center justify-center", "hover:cursor-pointer")}
                onClick={() => showPreview()}
              >
                <source srcSet={`${img.webp} 1x, ${img.webp2x} 2x`} type="image/webp" />
                <img srcSet={`${img.png} 1x, ${img.png2x} 2x`} src={img.png || ""} className="max-h-full max-w-full" />
              </picture>
            ))}
          </div>
          {controls && (
            <div
              className="button button-secondary flat icon-only z-10 mr-2 xs:m-0"
              onClick={() => buttonClick(slide + 1)}
              data-gtm="lp-image-slider-right"
            >
              <ChevronRightLarge />
            </div>
          )}
        </div>
        <div className="hidden justify-center pb-4 lg:flex">
          {images.map((_, i) => (
            <div
              key={i}
              className={cn("mx-1 mb-0 mt-2 h-3 w-3 cursor-pointer rounded-full", {
                "bg-primary": i === slide,
                "bg-white-10": i !== slide,
                invisible: !controls,
              })}
              onClick={() => buttonClick(i)}
              data-gtm="lp-image-slider-dot"
            />
          ))}
        </div>
      </div>

      {openPreview && <PreviewModal slides={slides} initialSlide={slide} close={close} />}
    </>
  )
}

export default Slideshow
